import React from 'react';
import { ChartConfig, ScientificChartConfig } from '../../types';
import { FieldPicker } from './FieldPicker';
import { FlaskConical, TrendingUp, Activity, Grid3X3, ArrowLeftRight, Box, Layers } from 'lucide-react';

interface ScientificMappingZoneProps {
    config: ChartConfig;
    onUpdateConfig: (updates: Partial<ChartConfig>) => void;
    onUpdateScientificConfig: (updates: Partial<ScientificChartConfig>) => void;
    allCols: string[];
    numericCols: string[];
    categoricalCols: string[];
}

// Convert string arrays to FieldPicker option format
const toFieldOptions = (cols: string[], isNumeric: boolean = false) =>
    cols.map(col => ({ label: col, value: col, isNumeric }));

const PLOT_TYPES = [
    { value: 'scatter', label: 'Scatter', icon: Activity },
    { value: 'line', label: 'Line', icon: TrendingUp },
    { value: 'vector', label: 'Vector Field', icon: ArrowLeftRight },
    { value: '3d-scatter', label: '3D Scatter', icon: Box },
    { value: '3d-surface', label: '3D Surface', icon: Layers },
    { value: '3d-wireframe', label: '3D Wireframe', icon: Layers },
] as const;

/**
 * Scientific Chart Field Mapping Zone
 * Provides field pickers and plot type selection for all scientific chart types
 */
export const ScientificMappingZone: React.FC<ScientificMappingZoneProps> = ({
    config,
    onUpdateConfig,
    onUpdateScientificConfig,
    allCols,
    numericCols,
    categoricalCols
}) => {
    const scientificConfig = config.advanced?.scientific;
    const plotType = scientificConfig?.plotType || 'scatter';
    const fieldMappings = scientificConfig?.fieldMappings || {};

    const updateFieldMapping = (field: string, value: string | undefined) => {
        onUpdateScientificConfig({
            fieldMappings: {
                ...fieldMappings,
                [field]: value || undefined
            }
        } as Partial<ScientificChartConfig>);
    };

    const is3D = plotType.startsWith('3d-');
    const isVector = plotType === 'vector';
    const isHeatmap = plotType === 'heatmap';
    const hasErrorBars = ['scatter', 'line'].includes(plotType) || scientificConfig?.errorBars?.show;

    return (
        <div className="space-y-4">
            {/* Plot Type Selector */}
            <div className="space-y-2">
                <h3 className="text-xs font-bold text-neon-blue/70 uppercase tracking-widest flex items-center gap-2">
                    <FlaskConical className="w-3.5 h-3.5" />
                    Plot Type
                </h3>
                <div className="grid grid-cols-4 gap-1">
                    {PLOT_TYPES.map(({ value, label, icon: Icon }) => (
                        <button
                            key={value}
                            onClick={() => onUpdateScientificConfig({ plotType: value as any })}
                            className={`p-2 rounded-lg border text-xs flex flex-col items-center gap-1 transition-all ${plotType === value
                                ? 'border-gold-500 bg-gold-500/10 text-gold-400'
                                : 'border-white/10 hover:border-white/20 text-gray-400 hover:text-gray-300'
                                }`}
                        >
                            <Icon className="w-4 h-4" />
                            <span className="text-[10px]">{label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="h-px bg-white/10 w-full" />

            {/* Core Field Mappings */}
            <div className="space-y-2">
                <h3 className="text-xs font-bold text-neon-blue/70 uppercase tracking-widest">
                    Axis Mapping
                </h3>

                <FieldPicker
                    label="X-Axis Field"
                    value={fieldMappings.xField}
                    onChange={(val) => updateFieldMapping('xField', val)}
                    options={toFieldOptions(allCols)}
                />

                <FieldPicker
                    label="Y-Axis Field"
                    value={fieldMappings.yField}
                    onChange={(val) => updateFieldMapping('yField', val)}
                    options={toFieldOptions(numericCols, true)}
                />

                {/* Z-Axis for 3D charts */}
                {is3D && (
                    <FieldPicker
                        label="Z-Axis Field"
                        value={fieldMappings.zField}
                        onChange={(val) => updateFieldMapping('zField', val)}
                        options={toFieldOptions(numericCols, true)}
                    />
                )}
            </div>

            <div className="h-px bg-white/10 w-full" />

            {/* Encoding Fields */}
            <div className="space-y-2">
                <h3 className="text-xs font-bold text-neon-blue/70 uppercase tracking-widest">
                    Encoding (Optional)
                </h3>

                <FieldPicker
                    label="Color Field"
                    value={fieldMappings.colorField}
                    onChange={(val) => updateFieldMapping('colorField', val)}
                    options={toFieldOptions(allCols)}
                    allowNone
                />

                {(plotType === 'scatter' || is3D) && (
                    <FieldPicker
                        label="Size Field"
                        value={fieldMappings.sizeField}
                        onChange={(val) => updateFieldMapping('sizeField', val)}
                        options={toFieldOptions(numericCols, true)}
                        allowNone
                    />
                )}

                {!is3D && !isHeatmap && (
                    <FieldPicker
                        label="Series Field (Split into multiple)"
                        value={fieldMappings.seriesField}
                        onChange={(val) => updateFieldMapping('seriesField', val)}
                        options={toFieldOptions(categoricalCols)}
                        allowNone
                    />
                )}
            </div>

            {/* Vector Field Specific */}
            {isVector && (
                <>
                    <div className="h-px bg-white/10 w-full" />
                    <div className="space-y-2">
                        <h3 className="text-xs font-bold text-neon-blue/70 uppercase tracking-widest">
                            Vector Components
                        </h3>

                        <FieldPicker
                            label="U Field (Horizontal)"
                            value={fieldMappings.uField}
                            onChange={(val) => updateFieldMapping('uField', val)}
                            options={toFieldOptions(numericCols, true)}
                        />

                        <FieldPicker
                            label="V Field (Vertical)"
                            value={fieldMappings.vField}
                            onChange={(val) => updateFieldMapping('vField', val)}
                            options={toFieldOptions(numericCols, true)}
                        />
                    </div>
                </>
            )}

            {/* Error Bar Fields */}
            {hasErrorBars && (
                <>
                    <div className="h-px bg-white/10 w-full" />
                    <div className="space-y-2">
                        <h3 className="text-xs font-bold text-neon-blue/70 uppercase tracking-widest">
                            Error Bars
                        </h3>

                        <FieldPicker
                            label="Y Error Field (±)"
                            value={fieldMappings.yErrorField}
                            onChange={(val) => updateFieldMapping('yErrorField', val)}
                            options={toFieldOptions(numericCols, true)}
                            allowNone
                        />

                        <FieldPicker
                            label="X Error Field (±)"
                            value={fieldMappings.xErrorField}
                            onChange={(val) => updateFieldMapping('xErrorField', val)}
                            options={toFieldOptions(numericCols, true)}
                            allowNone
                        />

                        <div className="text-[10px] text-gray-500 italic">
                            Or use exact bounds:
                        </div>

                        <div className="grid grid-cols-2 gap-2">
                            <FieldPicker
                                label="Y Lower Bound"
                                value={fieldMappings.yErrorLowerField}
                                onChange={(val) => updateFieldMapping('yErrorLowerField', val)}
                                options={toFieldOptions(numericCols, true)}
                                allowNone
                            />
                            <FieldPicker
                                label="Y Upper Bound"
                                value={fieldMappings.yErrorUpperField}
                                onChange={(val) => updateFieldMapping('yErrorUpperField', val)}
                                options={toFieldOptions(numericCols, true)}
                                allowNone
                            />
                        </div>
                    </div>
                </>
            )}

            {/* Line Plot Confidence Interval */}
            {plotType === 'line' && (
                <>
                    <div className="h-px bg-white/10 w-full" />
                    <div className="space-y-2">
                        <h3 className="text-xs font-bold text-neon-blue/70 uppercase tracking-widest">
                            Confidence Interval (Optional)
                        </h3>

                        <div className="grid grid-cols-2 gap-2">
                            <FieldPicker
                                label="Lower Bound"
                                value={fieldMappings.yLowerBoundField}
                                onChange={(val) => updateFieldMapping('yLowerBoundField', val)}
                                options={toFieldOptions(numericCols, true)}
                                allowNone
                            />
                            <FieldPicker
                                label="Upper Bound"
                                value={fieldMappings.yUpperBoundField}
                                onChange={(val) => updateFieldMapping('yUpperBoundField', val)}
                                options={toFieldOptions(numericCols, true)}
                                allowNone
                            />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ScientificMappingZone;
